import { useState } from "react";
import AppHeader from "@/components/AppHeader";
import { PRIVACY_COPY } from "@/constants/privacy";
import { PRIVACY_SURFACES } from "@/constants/privacyAudit";
import { ShieldCheck, Check, X, ChevronDown, ChevronUp } from "@/lib/icons";
import DailyDataResetButton from "@/components/DailyDataResetButton";
import NetworkGuardIndicator from "@/components/NetworkGuardIndicator";
import RouteNetworkReport from "@/components/RouteNetworkReport";
import Main from "@/components/Main";
import Seo from "@/components/Seo";

export default function Privacy() {
  const [changelogOpen, setChangelogOpen] = useState(false);
  return (
    <div className="min-h-screen bg-transparent">
      <Seo title="Privacy — ElementPad" description="How ElementPad handles your data. No tracking, no accounts, local-first storage. Read the full privacy notice." path="/privacy" />
      <AppHeader />
      <Main className="py-10">
        <article className="max-w-[760px] mx-auto px-4 space-y-8 text-foreground">
          <header>
            <h1 className="text-3xl font-bold mb-1">Privacy Policy</h1>
            <p className="text-muted-foreground text-sm">Last updated: May 25, 2026</p>
          </header>

          <section className="rounded-lg border border-primary/30 bg-primary/5 p-4">
            <h2 className="text-base font-semibold mb-1 text-foreground">
              {PRIVACY_COPY.shortTaglineHeadline}
            </h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {PRIVACY_COPY.noServerLong}
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed mt-2">
              ElementPad is an independent, personal project to make my teaching job easier. The goal is to unify scattered chemistry resources and help other teachers and learners in the most friendly way.
            </p>
          </section>

          <section aria-labelledby="privacy-verification-heading">
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck size={18} className="text-primary" aria-hidden="true" />
              <h2 id="privacy-verification-heading" className="text-xl font-semibold">
                Privacy Verification
              </h2>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed mb-4">
              A quick-reference look at what ElementPad does and does not do with your data.
            </p>

            <div className="overflow-x-auto rounded-lg border border-border">
              <table className="w-full text-sm">
                <tbody className="text-muted-foreground">
                  {[
                    { label: "Analytics", value: false },
                    { label: "Advertising or tracking cookies", value: false },
                    { label: "Functional infrastructure cookies", value: true },
                    { label: "Tracking pixels", value: false },
                    { label: "Browser fingerprinting", value: false },
                    { label: "Personal data collection", value: false },
                    { label: "Account or registration required", value: false },
                    { label: "Advertisements displayed", value: false },
                    { label: "Data sold or shared with third parties", value: false },
                    { label: "Cross-site tracking", value: false },
                    { label: "Local storage for preferences & progress", value: true },
                  ].map((row, i, arr) => (
                    <tr
                      key={row.label}
                      className={i < arr.length - 1 ? "border-b border-border" : ""}
                    >
                      <td className="px-3 py-2.5 text-foreground">{row.label}</td>
                      <td className="px-3 py-2.5">
                        {row.value ? (
                          <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-0.5 text-xs font-semibold text-emerald-400">
                            <Check size={12} aria-hidden="true" />
                            Yes
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 rounded-full border border-red-500/30 bg-red-500/10 px-2.5 py-0.5 text-xs font-semibold text-red-400">
                            <X size={12} aria-hidden="true" />
                            No
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="text-[11px] text-muted-foreground mt-3 leading-relaxed">
              "Yes" means the feature is present; "No" means it is entirely absent.
              Local storage and functional infrastructure cookies keep data on your device only
              and are not used for tracking or profiling.
            </p>
          </section>

          <section aria-labelledby="privacy-checklist-heading">
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck size={18} className="text-primary" aria-hidden="true" />
              <h2 id="privacy-checklist-heading" className="text-xl font-semibold">
                Privacy Verification Checklist
              </h2>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed mb-4">
              Every tab in ElementPad is audited against one rule:{" "}
              <strong className="text-foreground">
                local browser storage only, zero server transmission of user input
              </strong>
              . The table below lists each surface, the local-storage keys it
              touches, and confirms there are no outbound calls. You can verify
              this yourself in your browser's DevTools (Application tab for
              storage, Network tab for traffic) while using the site.
            </p>

            <div className="mb-4">
              <NetworkGuardIndicator />
            </div>

            <div className="mb-4">
              <RouteNetworkReport />
            </div>

            <div className="overflow-x-auto rounded-lg border border-border">
              <table className="w-full text-xs">
                <thead className="bg-muted/50 text-foreground">
                  <tr>
                    <th scope="col" className="text-left font-semibold px-3 py-2">
                      Surface
                    </th>
                    <th scope="col" className="text-left font-semibold px-3 py-2">
                      Local-storage keys
                    </th>
                    <th scope="col" className="text-left font-semibold px-3 py-2">
                      Network
                    </th>
                  </tr>
                </thead>
                <tbody className="text-muted-foreground">
                  {PRIVACY_SURFACES.map((s) => (
                    <tr key={s.name} className="border-t border-border align-top">
                      <td className="px-3 py-2">
                        <div className="font-semibold text-foreground">{s.name}</div>
                        <div className="text-[11px] text-muted-foreground mt-0.5">
                          {s.area}
                        </div>
                        <div className="text-[11px] text-muted-foreground mt-1 leading-snug">
                          {s.note}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        {s.storageKeys.length === 0 ? (
                          <span className="italic">None</span>
                        ) : (
                          <ul className="space-y-1">
                            {s.storageKeys.map((k) => (
                              <li
                                key={k}
                                className="font-mono text-[11px] leading-snug break-all"
                              >
                                {k}
                              </li>
                            ))}
                          </ul>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <span className="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-foreground">
                          <ShieldCheck size={10} aria-hidden="true" />
                          None
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="text-[11px] text-muted-foreground mt-3 leading-relaxed">
              Stored values are limited to preferences, dismissal flags, solo
              high-score numbers, and flashcard review state. None of them
              contain personal identifiers, and none are transmitted off your
              device. Clear them any time via your browser's site-data settings.
            </p>

            <DailyDataResetButton />
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-2">Overview</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              ElementPad ("we," "our," or "us") is a free educational chemistry platform built for teachers and
              chemistry learners. This Privacy Policy explains how we handle information when you visit{" "}
              elementpad.net. We are committed to keeping
              ElementPad simple, safe, and privacy-respecting.
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed mt-2">
              ElementPad is an independent, personal project to make my teaching job easier.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">Information We Collect</h2>

            <h3 className="text-base font-semibold mb-1">Information We DO NOT Collect</h3>
            <ul className="list-disc list-inside text-sm text-muted-foreground space-y-0.5 mb-4">
              <li>We do not collect personal information such as names, email addresses, or phone numbers</li>
              <li>We do not require user registration or account creation</li>
              <li>We do not store any user data in our databases - we don't operate a user database</li>
              <li>
                We do not save game results, nicknames, lab configurations, worksheets you generate,
                or your flashcard study progress on any server
              </li>
              <li>We do not track users across other websites or build user profiles</li>
              <li>We do not sell or share any data with third parties for marketing</li>
              <li>We do not display advertisements</li>
            </ul>

            <h3 className="text-base font-semibold mb-1">What stays on your device</h3>
            <p className="text-sm text-muted-foreground leading-relaxed mb-2">
              A few preferences are saved in your browser's local storage so the app remembers your
              setup between visits. This data <strong className="text-foreground">never leaves your device</strong>:
            </p>
            <ul className="list-disc list-inside text-sm text-muted-foreground space-y-0.5">
              <li>Accessibility preferences (text size, contrast, fonts)</li>
              <li>Theme choice (light/dark) and sound on/off preference</li>
              <li>Tool settings (lab configurations, worksheet selections, game options)</li>
              <li>Solo high scores for individual games (numbers only, no names)</li>
              <li>
                Learn-tab flashcard progress - Leitner box positions, review dates, and the
                "Missed from Play" review deck collected from games you play
              </li>
              <li>Per-game "how to play" dismissal flags and the privacy notice acknowledgement</li>
            </ul>
            <p className="text-sm text-muted-foreground leading-relaxed mt-2">
              You can clear it any time through your browser settings.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-2">How We Use Information</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Because we don't collect or save user information, there is nothing for us to use,
              analyze, or share. Our hosting provider may keep short-term request logs (see
              "Hosting" below) purely for security and to keep the Service online - we don't
              read, profile, or aggregate them for our own purposes.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-2">Local Storage</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              ElementPad uses your browser's local storage to remember your accessibility settings, theme preference,
              tool-specific options (such as lab configurations or worksheet templates), solo game high scores, and
              your Learn-tab flashcard progress (including the "Missed from Play" review deck). This data{" "}
              <strong className="text-foreground">never leaves your device</strong> and is not transmitted to our
              servers. You can clear it at any time through your browser settings.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">Third-Party Services</h2>

            <h3 className="text-base font-semibold mb-1">Analytics</h3>
            <p className="text-sm text-muted-foreground leading-relaxed mb-3">
              ElementPad does <strong className="text-foreground">not</strong> use any analytics service.
              There is no Google Analytics, Plausible, Fathom, Hotjar, Mixpanel, Segment, or similar
              tracker on this site. A built-in network guard wraps every outbound network API the
              browser exposes and logs each call to the DevTools console, so any accidental tracker
              would be visible immediately in the live counter above.
            </p>

            <h3 className="text-base font-semibold mb-1">Hosting</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              ElementPad is hosted on third-party infrastructure that may automatically log standard request data (such
              as IP address and user agent) for security and operational purposes.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">Cookies</h2>
            <p className="text-sm text-muted-foreground leading-relaxed mb-3">
              ElementPad does not set advertising, analytics, or tracking cookies.
              We do not use tracking pixels, fingerprinting scripts, or marketing tags.
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed mb-3">
              A small number of{" "}
              <strong className="text-foreground">functional / necessary cookies</strong>{" "}
              are placed by the infrastructure that keeps the site online and secure.
              These are required for the Service to work and are not used to identify
              you, build a profile, or follow you across other websites.
            </p>

            <h3 className="text-base font-semibold mb-1">Strictly Necessary Cookies</h3>
            <p className="text-sm text-muted-foreground leading-relaxed mb-3">
              These cookies are necessary for the website to function and cannot be switched off in our systems.
            </p>

            <div className="overflow-x-auto rounded-lg border border-border mb-3">
              <table className="w-full text-xs">
                <thead className="bg-muted/50 text-foreground">
                  <tr>
                    <th scope="col" className="text-left font-semibold px-3 py-2">Cookie</th>
                    <th scope="col" className="text-left font-semibold px-3 py-2">Provider</th>
                    <th scope="col" className="text-left font-semibold px-3 py-2">Purpose</th>
                    <th scope="col" className="text-left font-semibold px-3 py-2">Duration</th>
                  </tr>
                </thead>
                <tbody className="text-muted-foreground">
                  <tr className="border-t border-border align-top">
                    <td className="px-3 py-2 font-mono text-[11px]">__cf_bm</td>
                    <td className="px-3 py-2">Cloudflare (third party)</td>
                    <td className="px-3 py-2">Distinguishes humans from automated bots to protect the site from abuse and DDoS attacks</td>
                    <td className="px-3 py-2">30 minutes</td>
                  </tr>
                  <tr className="border-t border-border align-top">
                    <td className="px-3 py-2 font-mono text-[11px]">__dpl</td>
                    <td className="px-3 py-2">Hosting platform (third party)</td>
                    <td className="px-3 py-2">Keeps you on a consistent deployment version of the app during your visit so the site behaves predictably</td>
                    <td className="px-3 py-2">~7 days</td>
                  </tr>
                  <tr className="border-t border-border align-top">
                    <td className="px-3 py-2 font-mono text-[11px]">session-id</td>
                    <td className="px-3 py-2">ElementPad / hosting platform</td>
                    <td className="px-3 py-2">Maintains basic session continuity across page requests</td>
                    <td className="px-3 py-2">Session</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="text-sm text-muted-foreground leading-relaxed mb-3">
              None of these cookies contain personal identifiers, and none are shared
              with advertisers or used for cross-site tracking. You can view them at
              any time in your browser's DevTools (Application &rarr; Cookies) and
              clear them through your browser's site-data settings.
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed mb-3">
              For everything else, such as your preferences, progress, high scores,
              and flashcard state, ElementPad uses your browser's{" "}
              <strong className="text-foreground">local storage</strong> instead of
              cookies. Local storage is never transmitted with network requests and
              stays entirely on your device. See the verification table near the top
              of this page for the full list.
            </p>

            <h3 className="text-base font-semibold mb-1">A note on our "0 outbound calls" counter</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              The runtime counter above tracks outbound API calls made by ElementPad's
              own application code (<code className="font-mono text-[11px]">fetch</code>,{" "}
              <code className="font-mono text-[11px]">XMLHttpRequest</code>,{" "}
              <code className="font-mono text-[11px]">WebSocket</code>,{" "}
              <code className="font-mono text-[11px]">EventSource</code>,{" "}
              <code className="font-mono text-[11px]">sendBeacon</code>). It does not
              count the normal HTTP requests your browser makes to load the site
              itself. Those requests will carry the functional cookies listed above,
              which is expected and necessary for the site to function. If you open
              your browser's Network tab in DevTools, you'll see those page-load
              requests; what you won't see is ElementPad's code phoning home with
              your data, because it doesn't.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-2">Data Security</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              We implement appropriate security measures to protect against unauthorized access, alteration, disclosure,
              or destruction of information. However, no method of transmission over the internet is 100% secure.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-2">Children's Privacy</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              ElementPad is designed as an educational resource for chemistry learners and teachers. We do not knowingly
              collect personal information from children. Because we do not require accounts or collect personal data,
              the Service is generally safe.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-2">Changes to This Policy</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated
              "Last updated" date. Continued use of the Service after changes constitutes acceptance of the new policy.
            </p>
          </section>

          <section>
            <button
              onClick={() => setChangelogOpen((v) => !v)}
              className="w-full flex items-center justify-between group"
              aria-expanded={changelogOpen}
              aria-controls="privacy-changelog-panel"
            >
              <h2 className="text-xl font-semibold">Privacy Changelog</h2>
              {changelogOpen ? (
                <ChevronUp size={18} className="text-muted-foreground group-hover:text-foreground transition-colors" />
              ) : (
                <ChevronDown size={18} className="text-muted-foreground group-hover:text-foreground transition-colors" />
              )}
            </button>
            <p className="text-sm text-muted-foreground leading-relaxed mt-2 mb-3">
              Privacy change audit log.
            </p>
            <div
              id="privacy-changelog-panel"
              className={`space-y-3 overflow-hidden transition-all duration-300 ${changelogOpen ? "max-h-[2000px] opacity-100" : "max-h-0 opacity-0"}`}
            >
              <div className="rounded-lg border border-border bg-muted/30 p-3">
                <div className="flex items-center gap-2 mb-2">
                  <span className="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-foreground">
                    Current
                  </span>
                  <span className="text-xs text-muted-foreground">2026-05-25 — Cookie disclosure update</span>
                </div>
                <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                  <li>Added explicit cookie breakdown to the Privacy Policy: Cloudflare (<code className="font-mono text-[11px]">__cf_bm</code>), hosting platform (<code className="font-mono text-[11px]">__dpl</code>), and app session (<code className="font-mono text-[11px]">session-id</code>).</li>
                  <li>Updated verification table: split "Cookies" into "Advertising or tracking cookies" (No) and "Functional infrastructure cookies" (Yes).</li>
                  <li>Clarified that all cookies are functional / necessary and do not follow users across other websites.</li>
                </ul>
              </div>

              <div className="rounded-lg border border-border bg-muted/30 p-3">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs text-muted-foreground">2026-05-16 — Full privacy audit refresh</span>
                </div>
                <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                  <li>Catalogued every <code className="font-mono text-[11px]">localStorage</code> key across all surfaces in the verification table.</li>
                  <li>Re-ran the in-app network guard and confirmed zero outbound requests outside the host origin.</li>
                  <li>Re-ran the security header scan (CSP, X-Frame-Options, Referrer-Policy, Permissions-Policy).</li>
                  <li>Verified no analytics, cookies, tracking pixels, or fingerprinting scripts are present.</li>
                  <li>Confirmed no new data collection or third-party SDKs introduced this cycle.</li>
                </ul>
              </div>

              <div className="rounded-lg border border-border bg-muted/30 p-3">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs text-muted-foreground">2026-05-05 — Rubric share QR code</span>
                </div>
                <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                  <li>Verified QR code is generated entirely client-side (no third-party QR API).</li>
                  <li>Confirmed encoded payload contains only the share URL, no personal data.</li>
                  <li>Network tab inspection: no outbound requests on QR render.</li>
                </ul>
              </div>

              <div className="rounded-lg border border-border bg-muted/30 p-3">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs text-muted-foreground">2026-05-05 — Rubric share link + Web Share</span>
                </div>
                <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                  <li>Rubric state encoded into URL hash so sharing never round-trips through a server.</li>
                  <li>Web Share API used only on user gesture; no fallback to third-party share services.</li>
                  <li>No new <code className="font-mono text-[11px]">localStorage</code> keys added.</li>
                </ul>
              </div>

              <div className="rounded-lg border border-border bg-muted/30 p-3">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs text-muted-foreground">2026-05-05 — Rubric scoring form</span>
                </div>
                <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                  <li>All scoring inputs kept in component state; nothing persisted unless the teacher opts in.</li>
                  <li>Opt-in persistence writes only to the documented <code className="font-mono text-[11px]">rubric:*</code> local-storage keys.</li>
                  <li>No submission endpoint — form data never leaves the browser.</li>
                </ul>
              </div>

              <div className="rounded-lg border border-border bg-muted/30 p-3">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs text-muted-foreground">2026-05-05 — Teacher resource PDF/TXT downloads</span>
                </div>
                <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                  <li>Files generated in-browser via blob URLs; no download-tracking pixels.</li>
                  <li>No analytics events fired on download click.</li>
                  <li>Confirmed no filename or content is reported back to any server.</li>
                </ul>
              </div>

              <div className="rounded-lg border border-border bg-muted/30 p-3">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs text-muted-foreground">2026-05-05 — End-of-Year Project hub</span>
                </div>
                <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                  <li>New surface added to the verification table with its full <code className="font-mono text-[11px]">localStorage</code> key list.</li>
                  <li>Confirmed zero network egress while navigating the hub.</li>
                  <li>No personal identifiers stored — only progress flags and selection state.</li>
                </ul>
              </div>
            </div>
          </section>


          <section>
            <h2 className="text-xl font-semibold mb-2">Managing Local Storage</h2>
            <p className="text-sm text-muted-foreground leading-relaxed mb-2">
              You stay in full control of anything ElementPad stores on your device:
            </p>
            <ul className="list-disc list-inside text-sm text-muted-foreground space-y-0.5">
              <li>Use the "Clear all daily data" button above to wipe ElementPad's keys at once.</li>
              <li>Clear site data for elementpad.net from your browser's settings.</li>
              <li>Inspect every key in DevTools &rarr; Application &rarr; Local Storage.</li>
            </ul>
            <p className="text-sm text-muted-foreground leading-relaxed mt-2">
              Clearing local storage may reset your theme, accessibility preferences, tool
              settings, solo high scores, and flashcard progress - the app itself will keep
              working.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-2">Contact</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Questions about this policy? Reach us through the social media channels linked
              in the footer.
            </p>
          </section>
        </article>
      </Main>
    </div>
  );
}
